import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useIntl } from 'react-intl';
import messages from '../messages';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
    Box, Button, Typography, TextField, RadioGroup, FormControlLabel,
    Radio, Select, MenuItem, Autocomplete, CircularProgress, Checkbox, IconButton
} from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank, ChevronRight, ExpandMore, Folder, Refresh, Delete } from '@mui/icons-material';
import { ReactSVG } from 'react-svg';

import { getStyles } from './styles';
import { getAllExtractionDocumentTypes } from '../../../../utils/ApiFunctions/ExtractionData';
import { setExtractionDocumentTypesState } from '../../../../Redux/slice/dataSlice';
import LoadBar from '../../../../utils/LoadBar';
import { fetchUserEmail, getAgentApiToken, getUserItemId, getUserName } from '../../../../utils/GetAccountDetails';
import { tostAlert } from '../../../../utils/AlertToast';
import MonitoringServiceApi from '../../../../Api/Services/AmeyaBackend/MonitoringServiceApi';
import { apiErrorHandler } from '../../../../utils/ApiErrorHandler';
import AmeyaAuthApi from '../../../../Api/Services/AppflyteAuth/AmeyaAuthService';
import getSubscriptionsData from '../../../../utils/ApiFunctions/AppflyteSubscriptionsData';
import { useAppContext } from '../../../../context/AppContext';
import moment from 'moment';
import { setExtractionIntegrationAdded, setExtractionTriggerAdded } from '../../../../Redux/slice/newDataSlice';
import ExtractionTriggerApi from '../../../../Api/Services/AppflyteBackend/ExtractionTriggerApi';
import { IconSvg } from '../../../../utils/globalIcons';
import ExtractionIntegrationsApi from '../../../../Api/Services/AppflyteBackend/ExtractionIntegrationApi';

function AddTrigger({ connectors, connector_loading, setSelectedMenuItem }) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const intl = useIntl();

    const { selectedWorkspace, selectedProject, selectedOrganization } = useAppContext();
    const extraction_document_types = useSelector(state => state.all_data.extraction_document_types);
    const { trigger_data, action_type } = location.state || {};

    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [documentTypes, setDocumentTypes] = useState([]);

    const [allIntegrationData, setAllIntegrationData] = useState([]);
    const [webhookIntegrationData, setWebhookIntegrationData] = useState([]);

    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState(null);
    const [triggerName, setTriggerName] = useState('');
    const triggerPurpose = window.__ENV__.EXTRACTION_PIPELINE_ID ?? "";
    const [subjectMessage, setSubjectMessage] = useState('');
    const [importInterval, setImportInterval] = useState('five_min');
    const [importPreference, setImportPreference] = useState('all_files')
    const [importAction, setImportAction] = useState(null);
    const import_action = ['New file is created in source folder', 'File is modified in source folder']
    const [triggerDetails, setTriggerDetails] = useState(null)
    const [monitoringErrorStatus, setMonitoringErrorStatus] = useState(false)
    const [monitoringErrorMessage, setMonitoringErrorMessage] = useState('')

    const [googleAccessToken, setGoogleAccessToken] = useState(null);
    const [googleRefreshToken, setGoogleRefreshToken] = useState(null);
    const [azureAccessToken, setAzureAccessToken] = useState(null);
    const [azureRefreshToken, setAzureRefreshToken] = useState(null);
    const [allFolders, setAllFolders] = useState(null);
    const [folderSelected, setFolderSelected] = useState(null);
    const [selectedWebhooks, setSelectedWebhooks] = useState([]);
    const [schemaFields, setSchemaFields] = useState([]);
    const [schemaErrors, setSchemaErrors] = useState([]);

    useEffect(() => {
        getAllData();
    }, [])

    const getAllData = async () => {
        setLoading(true)
        try {

            const all_connectors = (connectors || [])?.filter(item => item?.payload?.type !== "webhook") ?? [];
            setAllIntegrationData(all_connectors)

            const filtered_webhook = (connectors || [])?.filter(item => item?.payload?.type === "webhook") ?? [];
            setWebhookIntegrationData(filtered_webhook)

            const all_doc_types = await getDocumentTypes()

            if (action_type === 'put') {

                const trigger_id = trigger_data?.payload?.__auto_id__ ?? null;
                const trigger_monitoring_status = trigger_data?.payload?.monitoring_status ?? false;
                const trigger_error_message = trigger_data?.payload?.error_message ?? '';

                const trigger_response = await fetchTriggerData(trigger_id)
                if (!trigger_response) {
                    navigate('/connectors/extraction-agent/triggers')
                }

                const triggerPayload = trigger_response?.payload ?? {};
                const trigger_type = triggerPayload?.type;
                const trigger_name = triggerPayload?.name;
                const trigger_subject_message = triggerPayload?.subject_message;
                const trigger_connector_id = triggerPayload?.connector_id ?? null;
                const trigger_document_type = triggerPayload?.document_type ?? null;
                const trigger_interval_seconds = triggerPayload?.interval_seconds ?? 300;
                const trigger_import_action = triggerPayload?.import_action;
                const trigger_import_preference = triggerPayload?.import_preference ?? "";
                const trigger_folder_id = triggerPayload?.folder_id ?? null;
                const trigger_webhooks = triggerPayload?.webhooks ?? [];
                const trigger_body_schemas = triggerPayload?.body_schema ?? {};

                const filtered_integration = (connectors || [])?.find(item => item?.payload?.__auto_id__ === trigger_connector_id)
                const filtered_doc_type = (all_doc_types || [])?.find(item => item?.payload?.__auto_id__ === trigger_document_type)
                const selected_Webhooks = (filtered_webhook || []).filter(item => trigger_webhooks.includes(item?.payload?.__auto_id__));
                const trigger_import_interval = Number(trigger_interval_seconds) == 300 ? 'five_min' : Number(trigger_interval_seconds) == 3600 ? 'hour' : 'five_min';

                if (trigger_type === 'google_drive') {
                    const folders_data = await handleFolderData(filtered_integration, 'google')
                    const filtered_folder = folders_data?.find(item => item?.id === trigger_folder_id) ?? null;
                    const folderTree = buildFolderTree(folders_data)
                    const all_folders = flattenTreeForAutocomplete(folderTree) ?? [];
                    setAllFolders(all_folders)
                    setFolderSelected(filtered_folder)
                }

                if (trigger_type === "one_drive") {
                    const folders_data = await handleFolderData(filtered_integration, 'azure')
                    const filtered_folder = folders_data?.find(item => item?.id === trigger_folder_id) ?? null;
                    setAllFolders(folders_data)
                    setFolderSelected(filtered_folder)
                }

                if (trigger_type === "gmail") {
                    const schemaFields = convertToSchemaFields(trigger_body_schemas);
                    setSchemaFields(schemaFields);
                    getRefreshToken(filtered_integration, 'google');
                }

                if (trigger_type === "outlook") {
                    const schemaFields = convertToSchemaFields(trigger_body_schemas);
                    setSchemaFields(schemaFields);
                    getRefreshToken(filtered_integration, 'azure');
                }

                setMonitoringErrorStatus(trigger_monitoring_status)
                setMonitoringErrorMessage(trigger_error_message)
                setTriggerDetails(trigger_response)
                setTriggerName(trigger_name)
                setSubjectMessage(trigger_subject_message)
                setSelectedDocumentType(filtered_doc_type)
                setSelectedIntegration(filtered_integration)
                setImportInterval(trigger_import_interval)
                setImportAction(trigger_import_action)
                setSelectedWebhooks(selected_Webhooks)
                trigger_import_preference === "Import all files present in the source folder" ? setImportPreference('all_files') : setImportPreference('new_files')
            }

        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const getDocumentTypes = async () => {
        try {
            let doc_type = []
            if (extraction_document_types?.length > 0) {
                doc_type = extraction_document_types
            } else {
                const project_id = selectedProject?.payload?.__auto_id__
                const response = await getAllExtractionDocumentTypes(project_id);
                doc_type = response
                dispatch(setExtractionDocumentTypesState(response))
            }
            setDocumentTypes(doc_type);
            return doc_type
        } catch (error) {
            console.log(error)
            return error
        }
    }

    const fetchTriggerData = async (item_id) => {
        try {
            const response = await ExtractionTriggerApi.getById(item_id);
            if (response.status === 200) {
                const responseData = response.data ?? null;
                return responseData;
            }
        } catch (error) {
            console.log(error)
            return null;
        }
    }

    useEffect(() => {
        if (selectedIntegration?.payload?.type === "google_drive" && action_type === 'post') {
            fetchGoogleFolderDetails(selectedIntegration)
        }
        if (selectedIntegration?.payload?.type === "one_drive" && action_type === 'post') {
            fetchOneDriveFolderDetails(selectedIntegration)
        }
        if (selectedIntegration?.payload?.type === "gmail" && action_type === 'post') {
            getRefreshToken(selectedIntegration, 'google');
        }
        if (selectedIntegration?.payload?.type === "outlook" && action_type === 'post') {
            getRefreshToken(selectedIntegration, 'azure');
        }
        setFolderSelected(null)
    }, [selectedIntegration])

    useEffect(() => {
        const errors = { ...formErrors };
        if (formErrors?.selectedIntegration && selectedIntegration !== null) { delete errors?.selectedIntegration }
        if (formErrors?.selectedDocumentType && selectedDocumentType !== null) { delete errors?.selectedDocumentType }
        if (formErrors?.folderSelected && folderSelected !== null) { delete errors?.folderSelected }
        if (formErrors?.importAction && importAction !== null) { delete errors?.importAction }
        if (formErrors?.triggerName && triggerName?.trim()) { delete errors?.triggerName }
        if (JSON.stringify(errors) !== JSON.stringify(formErrors)) { setFormErrors(errors) }
    }, [selectedIntegration, selectedDocumentType, triggerName, subjectMessage, folderSelected, importAction, formErrors]);

    const fetchGoogleFolderDetails = async (item) => {
        setDataLoading(true)
        try {
            const folder_response = await handleFolderData(item, 'google');
            const rootFolders = folder_response.map(folder => ({
                id: folder.id,
                label: folder.name,
                depth: 0,
                hasChildren: folder.hasChildren,
                childrenLoaded: false
            }));
            setAllFolders(rootFolders);
        } catch (error) {
            console.log(error)
        } finally {
            setDataLoading(false)
        }
    }

    const fetchOneDriveFolderDetails = async (item) => {
        setDataLoading(true)
        try {
            const folder_response = await handleFolderData(item, 'azure');
            const formattedFolders = folder_response.map(folder => ({
                ...folder,
                label: folder.label || folder.name || folder.id,
                id: folder.id
            })) ?? [];
            setAllFolders(formattedFolders);
        } catch (error) {
            console.log(error)
        } finally {
            setDataLoading(false)
        }
    }

    const flattenTreeForAutocomplete = (nodes, depth = 0) => {
        let result = [];
        for (let node of nodes) {
            result.push({
                label: `${' '.repeat(depth * 4)}${node.name}`,
                id: node.id,
                depth,
            });
            if (node.children?.length) {
                result = result.concat(flattenTreeForAutocomplete(node.children, depth + 1));
            }
        }
        return result;
    };

    function buildFolderTree(folders) {
        const idToFolder = {};
        const roots = [];
        folders.forEach(folder => { idToFolder[folder.id] = { ...folder, children: [] }; });
        folders.forEach(folder => {
            const parentId = folder.parents?.[0];
            if (parentId && idToFolder[parentId]) {
                idToFolder[parentId].children.push(idToFolder[folder.id]);
            } else {
                roots.push(idToFolder[folder.id]);
            }
        });

        return roots;
    }

    const getRefreshToken = async (item, provider) => {
        setDataLoading(true)
        try {
            const refresh_token = item?.payload?.refresh_token ?? null;

            if (!refresh_token) {
                throw new Error('No refresh token available');
            }

            const response = await AmeyaAuthApi.getTokens(refresh_token, provider);
            if (response.status === 200) {
                const responseData = response.data ?? null;
                if (!responseData) {
                    await updateIntegration(item)
                    dispatch(setExtractionIntegrationAdded(true))
                    tostAlert(intl.formatMessage(messages.source_token_error), 'error');
                    navigate('/connectors/extraction-agent/integrations')
                    setSelectedMenuItem(1)
                    return
                }
                const access_token = responseData?.access_token ?? null;
                const new_refresh_token = responseData?.refresh_token ?? null;
                provider === 'google' ? setGoogleAccessToken(access_token) : setAzureAccessToken(access_token)
                provider === 'google' ? setGoogleRefreshToken(new_refresh_token) : setAzureRefreshToken(new_refresh_token)
            }
            return
        } catch (error) {
            console.log(error)
            return
        } finally {
            setDataLoading(false)
        }
    }

    const handleFolderData = useCallback(async (item, connection_type) => {
        try {

            const refresh_token = item?.payload?.refresh_token ?? null;
            if (!refresh_token) {
                throw new Error('No refresh token available');
            }

            const response = await AmeyaAuthApi.getTokens(refresh_token, connection_type);
            const tokenData = response.data ?? null;
            if (!tokenData) {
                await updateIntegration(item)
                dispatch(setExtractionIntegrationAdded(true))
                tostAlert(intl.formatMessage(messages.source_token_error), 'error');
                navigate('/connectors/extraction-agent/integrations')
                setSelectedMenuItem(1)
                return
            }

            if (response.status !== 200) {
                throw new Error(`Failed to get tokens: ${response.status}`);
            }

            const { access_token, refresh_token: new_refresh_token } = tokenData || {};
            if (!access_token || !new_refresh_token) {
                throw new Error('Invalid tokens received');
            }

            connection_type === 'google' ? setGoogleAccessToken(access_token) : setAzureAccessToken(access_token)
            connection_type === "google" ? setGoogleRefreshToken(new_refresh_token) : setAzureRefreshToken(new_refresh_token)

            if (access_token && new_refresh_token) {
                const reqObj = { access_token };
                const drive_type = connection_type === "google" ? 'gdrive' : 'onedrive';
                const drive_response = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), drive_type);

                if (drive_response.status !== 200) {
                    throw new Error(`Failed to fetch folders: ${drive_response.status}`);
                }

                const all_folders = drive_response.data?.folders || [];
                if (all_folders.length === 0) {
                    console.warn('No folders found in OneDrive');
                }
                return all_folders
            }

        } catch (error) {
            console.error('OneDrive folder fetch error:', error);
            throw new Error(`Failed to get tokens: ${error}`);
        }
    }, []);

    // One Drive Folders
    const fetchOnedriveSubFolders = async (parentFolder) => {
        setDataLoading(true)
        try {

            const reqObj = { access_token: azureAccessToken, parent_id: parentFolder.id };
            const response = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), 'onedrive');

            if (response.status !== 200) return;

            const subfolders = response.data.folders.map(folder => ({
                ...folder,
                label: folder.name || folder.id,
                depth: (parentFolder.depth || 0) + 1,
                hasChildren: folder.hasChildren || false
            }));

            setAllFolders(prev => {
                const index = prev.findIndex(f => f.id === parentFolder.id);
                if (index === -1) return prev;
                return [
                    ...prev.slice(0, index + 1),
                    ...subfolders,
                    ...prev.slice(index + 1)
                ];
            });

        } catch (error) {
            console.log(error)
        } finally {
            setDataLoading(false)

        }
    };

    const toggleOneDriveFolderExpand = async (folder) => {
        if (folder.expanded) {
            setAllFolders(prev => prev.filter(f => f.parentId !== folder.id));
            return;
        }
        if (folder.hasChildren && !folder.childrenLoaded) {
            await fetchOnedriveSubFolders(folder, azureAccessToken);
        }
        setAllFolders(prev => prev.map(f => f.id === folder.id ? { ...f, expanded: true, childrenLoaded: true } : f));
    };

    // Google Drive Folders
    const fetchGoogleSubFolders = async (parentFolder) => {
        setDataLoading(true)
        try {
            const reqObj = {
                access_token: googleAccessToken,
                parent_id: parentFolder.id
            };

            const response = await AmeyaAuthApi.getFoldersDetails(JSON.stringify(reqObj), 'gdrive');

            const children = response.data.folders.map(folder => ({
                id: folder.id,
                label: folder.name,
                parentId: parentFolder.id,
                depth: (parentFolder.depth || 0) + 1,
                hasChildren: folder.hasChildren
            }));
            setAllFolders(prev => [...prev, ...children]);
            parentFolder.childrenLoaded = true;

        } catch (err) {
            console.error(err);
        } finally {
            setDataLoading(false)
        }
    };

    const toggleGDriveFolderExpand = async (folder) => {
        if (folder.expanded) {
            setAllFolders(prev => prev.filter(f => f.parentId !== folder.id)
                .map(f => f.id === folder.id ? { ...f, expanded: false } : f)
            );
            return;
        }

        if (folder.hasChildren && !folder.childrenLoaded) {
            await fetchGoogleSubFolders(folder);
        }
        setAllFolders(prev => prev.map(f => f.id === folder.id ? { ...f, expanded: true, childrenLoaded: true } : f));
    };

    const handleAddField = () => {
        setSchemaFields(prev => [
            ...prev,
            { fieldName: '', description: '' }
        ]);
    };

    const handleChange = (index, key, value) => {
        const updatedFields = [...schemaFields];
        updatedFields[index][key] = value;
        setSchemaFields(updatedFields);
        const updatedErrors = [...schemaErrors];
        if (updatedErrors[index]) {
            if (value.trim()) {
                updatedErrors[index][key] = "";
            }
        }
        setSchemaErrors(updatedErrors);
    };

    const handleDelete = (index) => {
        setSchemaFields(prev => prev.filter((_, i) => i !== index));
    };

    const convertToBodySchema = (schema_fields = []) => {
        return schema_fields.reduce((acc, field) => {
            if (field.fieldName && field.description) {
                acc[field.fieldName] = field.description;
            }
            return acc;
        }, {});
    };

    const convertToSchemaFields = (bodySchema) => {
        if (!bodySchema) return [];

        return Object.entries(bodySchema).map(([key, value]) => ({
            fieldName: key,
            description: value
        }));
    };

    const handleMonitorService = async (trigger_data) => {
        try {

            const document_type = selectedDocumentType?.payload?.__auto_id__ ?? "";
            const interval_seconds = (importInterval === 'five_min') ? 300 : (importInterval === 'hour') ? 3600 : 0;
            const alertEmail = await fetchUserEmail();

            const subscriptions = await getSubscriptionsData();
            const subscriptionData = subscriptions?.[0] ?? {};
            const ameya_auth_config = subscriptionData?.payload?.ameya_auth_config ?? {};

            const email = trigger_data?.email ?? null;
            const job_id = trigger_data?.job_id ?? null;
            const folder_id = trigger_data?.folder_id ?? null;
            const webhooks = trigger_data?.webhooks ?? [];
            const dataset_id = trigger_data?.__auto_id__;
            const connection_id = trigger_data?.connector_id;
            const body_schema = trigger_data?.body_schema;

            const token = await getAgentApiToken();
            const project_id = selectedProject?.payload?.__auto_id__;

            const connectionObj = {}

            if (selectedIntegration?.payload?.type === "google_drive") {

                const client_id = ameya_auth_config['google']?.client_id ?? null;
                const client_secret = ameya_auth_config['google']?.client_secret ?? null;

                connectionObj.google_drive = [{
                    "job_id": job_id,
                    "client_id": client_id,
                    "folder_id": folder_id,
                    "connection_id": connection_id,
                    "access_token": googleAccessToken,
                    "refresh_token": googleRefreshToken,
                    "client_secret": client_secret,
                    "interval_seconds": interval_seconds,
                    "source_id": webhooks
                }]

            }

            if (selectedIntegration?.payload?.type === "one_drive") {

                const client_id = ameya_auth_config['azure']?.client_id ?? null;
                const tenant_id = ameya_auth_config['azure']?.tenant_id ?? null;
                const client_secret = ameya_auth_config['azure']?.client_secret ?? null;

                connectionObj.one_drive = [{
                    "job_id": job_id,
                    "user_id": email,
                    "folder_id": folder_id,
                    "client_id": client_id,
                    "tenant_id": tenant_id,
                    "connection_id": connection_id,
                    "client_secret": client_secret,
                    "refresh_token": azureRefreshToken,
                    "access_token": azureAccessToken,
                    "interval_seconds": interval_seconds,
                    "source_id": webhooks
                }]
            }

            if (selectedIntegration?.payload?.type === "gmail") {
                const client_id = ameya_auth_config['google']?.client_id ?? null;
                const client_secret = ameya_auth_config['google']?.client_secret ?? null;

                connectionObj.google_email = {
                    "job_id": job_id,
                    "access_token": googleAccessToken,
                    "refresh_token": googleRefreshToken,
                    "client_id": client_id,
                    "client_secret": client_secret,
                    "target_emails": [email],
                    "subject_message": subjectMessage,
                    "connection_id": connection_id,
                    "interval_seconds": interval_seconds,
                    "source_id": webhooks,
                    "body_schema": body_schema
                }
            }

            if (selectedIntegration?.payload?.type === "outlook") {

                const client_id = ameya_auth_config['azure']?.client_id ?? null;
                const tenant_id = ameya_auth_config['azure']?.tenant_id ?? null;
                const client_secret = ameya_auth_config['azure']?.client_secret ?? null;

                connectionObj.outlook_email = {
                    "job_id": job_id,
                    "client_id": client_id,
                    "target_emails": [email],
                    "client_secret": client_secret,
                    "tenant_id": tenant_id,
                    "subject_message": subjectMessage,
                    "connection_id": connection_id,
                    "access_token": azureAccessToken,
                    "refresh_token": azureRefreshToken,
                    "interval_seconds": interval_seconds,
                    "source_id": webhooks,
                    "body_schema": body_schema
                }
            }

            const reqObj = {
                "auth_token": token,
                "alert_email": alertEmail,
                "dataset_id": dataset_id,
                "job_type": "ex",
                "document_type": document_type,
                "purpose": triggerPurpose,
                "connections": connectionObj
            }
            const response = await MonitoringServiceApi.createJob(JSON.stringify(reqObj), project_id);
            return response;
        } catch (error) {
            console.log(error)
        }
    }

    const handleSave = async () => {
        setLoading(true)
        try {
            const errors = {};
            let newErrors = [];

            if (!triggerName?.trim()) { errors.triggerName = 'This Field is required' }

            if (selectedIntegration === null) { errors.selectedIntegration = 'This Field is required' }
            if (selectedDocumentType === null) { errors.selectedDocumentType = 'This Field is required' }

            if (selectedIntegration?.payload?.type === "gmail" || selectedIntegration?.payload?.type === "outlook") {
                if (!subjectMessage?.trim()) { errors.subjectMessage = 'This Field is required' }
                schemaFields.forEach((field, index) => {
                    let error = {};

                    if (field.fieldName && !field.description) {
                        error.description = "Description is required";
                    }

                    if (!field.fieldName && field.description) {
                        error.fieldName = "Field Name is required";
                    }

                    newErrors[index] = error;
                });
                setSchemaErrors(newErrors);
                const hasError = newErrors.some(err => Object.keys(err).length > 0);
                if (hasError) return;
            }

            if (selectedIntegration?.payload?.type === "google_drive" || selectedIntegration?.payload?.type === "one_drive") {
                if (folderSelected === null) { errors.folderSelected = 'This Field is required' }
                if (importAction === null) { errors.importAction = 'This Field is required' }
            }

            if (Object.keys(errors).length === 0) {
                action_type === "post" ? await handleAddTrigger() : await handleUpdatedTrigger()
            } else {
                setFormErrors(errors);
                return
            }

        } catch (error) {
            console.log(error)
            apiErrorHandler(error)
        } finally {
            setLoading(false)
        }
    }

    const handleAddTrigger = async () => {
        try {

            const userId = await getUserItemId();
            const document_type_id = selectedDocumentType?.payload?.__auto_id__ ?? "";
            const alertEmail = await fetchUserEmail();
            const interval_seconds = (importInterval === 'five_min') ? 300 : (importInterval === 'hour') ? 3600 : 0;
            const import_preference = importPreference === "all_files" ? "Import all files present in the source folder" : "Import only new files created in the source folder after this trigger is activated";
            const folder_id = folderSelected?.id ?? null;

            const integration_id = selectedIntegration?.payload?.__auto_id__ ?? null;
            const integration_name = selectedIntegration?.payload?.name ?? null;
            const integration_type = selectedIntegration?.payload?.type ?? null;
            const integration_email = selectedIntegration?.payload?.email ?? null;
            const integration_user_id = selectedIntegration?.payload?.user_id ?? null;
            const integration_auth_required = selectedIntegration?.payload?.auth_required ?? true;
            const integration_access_key = selectedIntegration?.payload?.access_key ?? null;
            const integration_secret_access_key = selectedIntegration?.payload?.secret_access_key ?? null;
            const integration_endpoint_url = selectedIntegration?.payload?.endpoint_url ?? null;
            const trigger_webhooks = (selectedWebhooks || [])?.map(item => item?.payload?.__auto_id__) ?? [];
            const bodySchema = convertToBodySchema(schemaFields);

            const collection_item = {
                name: triggerName,
                type: integration_type,
                connector_id: integration_id,
                connector_name: integration_name,
                document_type: document_type_id,
                alert_email: alertEmail,
                purpose: triggerPurpose,
                interval_seconds: interval_seconds,
                auth_required: integration_auth_required,
                enabled: true,
                status: "active",
                extraction_integrations: [integration_id],
                webhooks: trigger_webhooks,
                organization_id: selectedOrganization?.payload?.__auto_id__ ?? null,
                workspace_id: selectedWorkspace?.payload?.__auto_id__ ?? null,
                project_id: selectedProject?.payload?.__auto_id__ ?? null,
                created_on: moment().format('YYYY-MM-DD HH:mm:ss'),
                created_by: userId,
                job_id: uuid(),
                __auto_id__: uuid()
            }

            if (integration_type === "google_drive") {
                collection_item.email = integration_email;
                collection_item.user_id = integration_user_id;
                collection_item.folder_id = folder_id;
                collection_item.refresh_token = googleRefreshToken;
                collection_item.import_preference = import_preference;
                collection_item.import_action = importAction;
            }

            if (integration_type === "one_drive") {
                collection_item.email = integration_email;
                collection_item.user_id = integration_user_id;
                collection_item.folder_id = folder_id;
                collection_item.refresh_token = azureRefreshToken;
                collection_item.import_preference = import_preference;
                collection_item.import_action = importAction;
            }

            if (integration_type === "gmail") {
                collection_item.email = integration_email;
                collection_item.user_id = integration_user_id;
                collection_item.refresh_token = googleRefreshToken;
                collection_item.subject_message = subjectMessage;
                collection_item.body_schema = bodySchema;
            }

            if (integration_type === "outlook") {
                collection_item.email = integration_email;
                collection_item.user_id = integration_user_id;
                collection_item.refresh_token = azureRefreshToken;
                collection_item.subject_message = subjectMessage;
                collection_item.body_schema = bodySchema;
            }

            if (integration_type === "aws_s3" || integration_type === "azure_blob") {
                collection_item.access_key = integration_access_key;
                collection_item.secret_access_key = integration_secret_access_key;
                collection_item.endpoint_url = integration_endpoint_url;
            }

            const collection_data = await ExtractionTriggerApi.checkNameExistence(triggerName)
            if (collection_data.data && collection_data.data.published_collections_detail.length > 0) {
                const existing_data = collection_data.data.published_collections_detail.flatMap(collection => collection_data.data[collection.id]);
                if (existing_data) {
                    const filtered_data = existing_data?.find(item => item?.payload?.project_id === selectedProject?.payload?.__auto_id__ && item?.payload?.name === triggerName) ?? null
                    if (filtered_data) {
                        tostAlert(intl.formatMessage(messages.trigger_name_exist), 'warning')
                        return
                    }
                }
            }

            if (["google_drive", "one_drive", "gmail", "outlook"].includes(integration_type)) {
                const monitor_response = await handleMonitorService(collection_item);
                if (monitor_response?.status !== 200 && monitor_response?.status !== 201) {
                    tostAlert(intl.formatMessage(messages.trigger_add_error), 'error');
                    return;
                }
            }

            const reqObj = {
                collection_item: collection_item
            }

            const response = await ExtractionTriggerApi.addData(JSON.stringify(reqObj));
            if (response.status === 200) {
                tostAlert(intl.formatMessage(messages.trigger_add_success), "success");
                dispatch(setExtractionTriggerAdded(true))
                dispatch(setExtractionIntegrationAdded(true))
                navigate('/connectors/extraction-agent/triggers')
            } else {
                tostAlert(intl.formatMessage(messages.trigger_add_error), 'error');
            }

        } catch (error) {
            console.log(error)
        }
    }

    const handleUpdatedTrigger = async () => {
        try {

            const document_type_id = selectedDocumentType?.payload?.__auto_id__ ?? "";
            const alertEmail = await fetchUserEmail();
            const interval_seconds = (importInterval === 'five_min') ? 300 : (importInterval === 'hour') ? 3600 : 0;
            const import_preference = importPreference === "all_files" ? "Import all files present in the source folder" : "Import only new files created in the source folder after this trigger is activated";
            const folder_id = folderSelected?.id ?? null;

            const integration_id = selectedIntegration?.payload?.__auto_id__ ?? null;
            const integration_name = selectedIntegration?.payload?.name ?? null;
            const integration_type = selectedIntegration?.payload?.type ?? null;
            const integration_email = selectedIntegration?.payload?.email ?? null;
            const integration_user_id = selectedIntegration?.payload?.user_id ?? null;
            const trigger_webhooks = (selectedWebhooks || [])?.map(item => item?.payload?.__auto_id__) ?? [];
            const bodySchema = convertToBodySchema(schemaFields);

            const dataset_id = triggerDetails?.payload?.__auto_id__ ?? null;
            const dataset_update_key = triggerDetails?.update_key ?? null;
            const existing_job_id = triggerDetails?.payload?.job_id ?? null;
            const new_job_id = uuid()

            const collection_item = {
                __auto_id__: dataset_id,
                email: integration_email,
                folder_id: folder_id,
                webhooks: trigger_webhooks,
                connector_id: integration_id,
                body_schema: bodySchema,
                job_id: new_job_id
            }

            const reqObj = {
                "id": dataset_id,
                "fields": [
                    { "path": "$.name", "value": triggerName },
                    { "path": "$.connector_id", "value": integration_id },
                    { "path": "$.connector_name", "value": integration_name },
                    { "path": "$.document_type", "value": document_type_id },
                    { "path": "$.alert_email", "value": alertEmail },
                    { "path": "$.email", "value": integration_email },
                    { "path": "$.user_id", "value": integration_user_id },
                    { "path": "$.purpose", "value": triggerPurpose },
                    { "path": "$.interval_seconds", "value": interval_seconds },
                    { "path": "$.extraction_integrations", "value": [integration_id] },
                    { "path": "$.webhooks", "value": trigger_webhooks },
                    { "path": "$.job_id", "value": new_job_id },
                    { "path": "$.refresh_token", "value": (integration_type === "google_drive" || integration_type === "gmail") ? googleRefreshToken : azureRefreshToken },
                    { "path": "$.updated_on", "value": moment().format('YYYY-MM-DD HH:mm:ss') }
                ]
            }

            if (integration_type === "google_drive" || integration_type === "one_drive") {
                reqObj.fields.push({ "path": "$.folder_id", "value": folder_id })
                reqObj.fields.push({ "path": "$.import_preference", "value": import_preference })
                reqObj.fields.push({ "path": "$.import_action", "value": importAction })
            }

            if (integration_type === "gmail" || integration_type === "outlook") {
                reqObj.fields.push({ "path": "$.body_schema", "value": bodySchema })
                reqObj.fields.push({ "path": "$.subject_message", "value": subjectMessage })
            }

            const collection_data = await ExtractionTriggerApi.checkNameExistence(triggerName)
            if (collection_data.data && collection_data.data.published_collections_detail.length > 0) {
                const existing_data = collection_data.data.published_collections_detail.flatMap(collection => collection_data.data[collection.id]);

                if (existing_data?.length) {
                    const filtered_data = existing_data.find(item =>
                        item?.payload?.project_id === selectedProject?.payload?.__auto_id__ &&
                        item?.payload?.name === triggerName &&
                        item?.payload?.__auto_id__ !== dataset_id
                    );
                    if (filtered_data) {
                        tostAlert(intl.formatMessage(messages.trigger_name_exist), 'warning')
                        return;
                    }
                }
            }

            if (["google_drive", "one_drive", "gmail", "outlook"].includes(integration_type)) {

                try {
                    await MonitoringServiceApi.deleteJobs(JSON.stringify({ "job_ids": existing_job_id }));
                } catch (error) {
                    console.log(error)
                    // const error_response = error?.response?.data?.detail ?? null;
                    // if (error_response !== `Job ${existing_job_id} not found`) {
                    //     tostAlert(intl.formatMessage(messages.trigger_update_error), 'error');
                    //     return
                    // }
                }

                const monitor_response = await handleMonitorService(collection_item);
                if (monitor_response?.status !== 200 && monitor_response?.status !== 201) {
                    tostAlert(intl.formatMessage(messages.trigger_update_error), 'error');
                    return;
                }
            }

            const response = await ExtractionTriggerApi.update(JSON.stringify(reqObj), dataset_id, dataset_update_key);
            if (response.status === 200) {
                tostAlert(intl.formatMessage(messages.trigger_update_success), "success");
                dispatch(setExtractionTriggerAdded(true))
                dispatch(setExtractionIntegrationAdded(true))
                navigate('/connectors/extraction-agent/triggers')
            } else {
                tostAlert(intl.formatMessage(messages.trigger_update_error), 'error');
            }


        } catch (error) {
            console.log(error)
        }
    }

    const updateIntegration = async (data) => {
        try {
            const integration_id = data?.payload?.__auto_id__;
            const update_key = data?.update_key
            const reqObj = {
                "id": integration_id,
                "fields": [
                    { "path": "$.auth_status", "value": "expired" },
                    { "path": "$.updated_on", "value": moment().format('YYYY-MM-DD HH:mm:ss') }
                ]
            }
            const res = await ExtractionIntegrationsApi.update(JSON.stringify(reqObj), integration_id, update_key);
            if (res.status === 200) {
                dispatch(setExtractionIntegrationAdded(true))
            }
            return
        } catch (error) {
            console.log(error)
            return
        }
    }

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>

                {(loading || dataLoading) && <LoadBar />}

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={styles.mainHeadingText}>Add Trigger</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            sx={{ ...styles.saveBtn, width: '100px' }}
                            onClick={handleSave}
                            disabled={loading || dataLoading || connector_loading}
                        >
                            <Typography sx={{ ...styles.btnText, color: '#FFFFFF' }}>SAVE</Typography>
                        </Button>
                        <Button
                            sx={{ ...styles.cancelBtn, width: '100px' }}
                            onClick={() => navigate('/connectors/extraction-agent/triggers')}
                        >
                            <Typography sx={{ ...styles.btnText, color: '#000000' }}>CANCEL</Typography>
                        </Button>
                    </Box>
                </Box>

                {monitoringErrorStatus &&
                    (<Typography sx={{ fontSize: '14px', fontWeight: '400', color: "#C30E2E" }}>
                        {monitoringErrorMessage}
                    </Typography>)
                }

                <Box sx={{ paddingBottom: '20px', marginTop: '20px' }}>

                    <Box mb={2}>
                        <Typography mb={1}>Select a Integration <span style={{ color: 'red' }}>*</span></Typography>
                        <Select
                            variant="outlined"
                            size="small"
                            sx={{ width: '600px' }}
                            value={selectedIntegration}
                            disabled={action_type === "put" || connector_loading}
                            onChange={(e) => setSelectedIntegration(e.target.value)}
                            displayEmpty
                            renderValue={(selected) => selected ? selected?.payload?.name : "Select Integration"}
                        >
                            <MenuItem value="" disabled>
                                <Typography variant="body1">Select Integration</Typography>
                            </MenuItem>
                            {(allIntegrationData || []).length > 0 ? (
                                (allIntegrationData || []).map((item, index) => (
                                    <MenuItem key={index} value={item}>
                                        <Typography variant="body1">{item?.payload?.name} ({item?.payload?.type}) </Typography>
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value="" disabled>
                                    <Typography variant="body1">No Integration Available</Typography>
                                </MenuItem>
                            )}
                        </Select>
                        {formErrors.selectedIntegration && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.selectedIntegration}</Typography>}
                    </Box>


                    {selectedIntegration &&
                        <Box>

                            <Box mt={2}>
                                <Typography mb={1}>Trigger Name <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    variant="outlined"
                                    type="text"
                                    size='small'
                                    sx={{ width: '600px' }}
                                    placeholder='Trigger Name'
                                    value={triggerName}
                                    onChange={(e) => setTriggerName(e.target.value)}
                                />
                                {formErrors.triggerName && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.triggerName}</Typography>}
                            </Box>

                            <Box mt={2}>
                                <Typography mb={1}>Assign Trigger to Document Type <span style={{ color: 'red' }}>*</span></Typography>
                                <Select
                                    variant="outlined"
                                    size='small'
                                    sx={{ width: '600px' }}
                                    value={selectedDocumentType}
                                    onChange={(e) => setSelectedDocumentType(e.target.value)}
                                    displayEmpty
                                    renderValue={(selected) => selected ? selected?.payload?.name : "Select DocumentType"}
                                >
                                    <MenuItem value="" disabled>
                                        <Typography variant="body1">Select Document Type</Typography>
                                    </MenuItem>
                                    {documentTypes.length > 0 ? (
                                        documentTypes.map((item, index) => (
                                            <MenuItem key={index} value={item}>
                                                <Typography variant="body1">{item?.payload?.name}</Typography>
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem value="" disabled>
                                            <Typography variant="body1">No Document Type Available</Typography>
                                        </MenuItem>
                                    )}
                                </Select>
                                {formErrors.selectedDocumentType && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.selectedDocumentType}</Typography>}
                            </Box>

                            {(selectedIntegration?.payload?.type === "google_drive" || selectedIntegration?.payload?.type === "one_drive") &&
                                (<Box>
                                    <Box sx={{ border: '1px solid #D9D9D9', borderRadius: '5px', padding: '15px', marginTop: '20px', width: '600px' }}>
                                        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                                            <Typography sx={{ fontSize: "15px", fontWeight: "600" }}>Import Source</Typography>
                                            {!dataLoading ?
                                                (<IconButton onClick={() => {
                                                    selectedIntegration?.payload?.type === "google_drive"
                                                        ?
                                                        fetchGoogleFolderDetails(selectedIntegration)
                                                        :
                                                        fetchOneDriveFolderDetails(selectedIntegration)
                                                }}
                                                >
                                                    <Refresh />
                                                </IconButton>)
                                                :
                                                (<CircularProgress size={'20px'} />)
                                            }
                                        </Box>

                                        {selectedIntegration?.payload?.type === "google_drive" &&
                                            <Box mt={2}>
                                                <Typography mb={1}>Import File(s) From <span style={{ color: 'red' }}>*</span></Typography>
                                                <Autocomplete
                                                    disabled={dataLoading || loading}
                                                    size="small"
                                                    options={allFolders || []}
                                                    value={folderSelected}
                                                    getOptionLabel={(option) => option.label}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder="Select Source Folder" />
                                                    )}
                                                    onChange={(e, newValue) => setFolderSelected(newValue)}
                                                    renderOption={(props, option) => (
                                                        <li {...props} style={{ paddingLeft: 8 + option.depth * 22 }}>
                                                            {option.hasChildren && (
                                                                <span
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleGDriveFolderExpand(option);
                                                                    }}
                                                                    style={{ cursor: 'pointer', marginRight: 6 }}
                                                                >
                                                                    {option.expanded
                                                                        ? <ExpandMore fontSize="small" />
                                                                        : <ChevronRight fontSize="small" />
                                                                    }
                                                                </span>
                                                            )}

                                                            <Folder fontSize="small" sx={{ mr: 1 }} />
                                                            <Typography sx={{ fontSize: 14 }}>
                                                                {option.label}
                                                            </Typography>
                                                        </li>
                                                    )}
                                                />
                                                {formErrors.folderSelected && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.folderSelected}</Typography>}
                                            </Box>
                                        }

                                        {selectedIntegration?.payload?.type === "one_drive" &&
                                            <Box mt={2}>
                                                <Typography mb={1}>Import File(s) From <span style={{ color: 'red' }}>*</span></Typography>
                                                <Autocomplete
                                                    disabled={dataLoading || loading}
                                                    size='small'
                                                    options={allFolders || []}
                                                    value={folderSelected}
                                                    getOptionLabel={(option) => option?.name || ''}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder="Select Source Folder" />
                                                    )}
                                                    onChange={(e, newValue) => { setFolderSelected(newValue) }}
                                                    renderOption={(props, option) => (
                                                        <li
                                                            {...props}
                                                            style={{
                                                                paddingLeft: 8 + (option.depth || 0) * 22,
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            {option.hasChildren && (
                                                                <span
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleOneDriveFolderExpand(option);
                                                                    }}
                                                                    style={{ cursor: 'pointer', marginRight: 6 }}
                                                                >
                                                                    {option.expanded
                                                                        ? <ExpandMore fontSize="small" />
                                                                        : <ChevronRight fontSize="small" />
                                                                    }
                                                                </span>
                                                            )}
                                                            <Folder fontSize="small" sx={{ mr: 1 }} />
                                                            <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
                                                                {option.label}
                                                            </Typography>
                                                        </li>
                                                    )}
                                                />
                                                {formErrors.folderSelected && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.folderSelected}</Typography>}
                                            </Box>
                                        }

                                        <Box mt={2}>
                                            <Typography marginBottom={'4px'}>Import Preference <span style={{ color: 'red' }}>*</span></Typography>
                                            <RadioGroup
                                                aria-labelledby="demo-radio-buttons-group-label"
                                                value={importPreference}
                                                onChange={(e) => setImportPreference(e.target.value)}
                                            >
                                                <FormControlLabel value="all_files"
                                                    control={<Radio sx={{ color: '#0B51C5' }} />} label="Import all files present in the source folder"
                                                />
                                                <FormControlLabel value="new_files"
                                                    control={<Radio sx={{ color: '#0B51C5' }} />} label="Import only new files created in the source folder after this trigger is activated"
                                                />
                                            </RadioGroup>
                                        </Box>

                                    </Box>
                                    <Box sx={{ border: '1px solid #D9D9D9', borderRadius: '5px', padding: '15px', marginTop: '20px', width: '600px' }}>
                                        <Typography sx={{ fontSize: "15px", fontWeight: "600" }}>Import Action</Typography>
                                        <Box mt={2}>
                                            <Typography mb={1}>Import New File(s) When <span style={{ color: 'red' }}>*</span></Typography>
                                            <Select
                                                variant="outlined"
                                                size='small'
                                                sx={{ width: '565px' }}
                                                value={importAction}
                                                onChange={(e) => setImportAction(e.target.value)}
                                                displayEmpty
                                                renderValue={(selected) => selected ? selected : "Select Import Action"}
                                            >
                                                {import_action.map((item, index) => (
                                                    <MenuItem key={index} value={item}>
                                                        <Typography variant="body1">{item}</Typography>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {formErrors.importAction && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.importAction}</Typography>}
                                        </Box>
                                        <Box mt={2}>
                                            <Typography marginBottom={'4px'}>Check for Import Every <span style={{ color: 'red' }}>*</span></Typography>
                                            <RadioGroup
                                                aria-labelledby="demo-radio-buttons-group-label"
                                                value={importInterval}
                                                onChange={(e) => setImportInterval(e.target.value)}
                                            >
                                                <FormControlLabel value="five_min" control={<Radio sx={{ color: '#0B51C5' }} />} label="5 minutes" />
                                                <FormControlLabel value="hour" control={<Radio sx={{ color: '#0B51C5' }} />} label="1 hour" />
                                            </RadioGroup>
                                        </Box>
                                    </Box>
                                </Box>)
                            }


                            {(selectedIntegration?.payload?.type === "gmail" || selectedIntegration?.payload?.type === "outlook") &&
                                (<Box mb={3}>
                                    <Box sx={{ marginTop: '20px', width: '600px' }}>
                                        <Typography mb={1}>Subject Message <span style={{ color: 'red' }}>*</span></Typography>
                                        <TextField
                                            variant="outlined"
                                            type="text"
                                            size='medium'
                                            sx={{ width: '600px' }}
                                            placeholder='Subject Message'
                                            value={subjectMessage}
                                            onChange={(e) => setSubjectMessage(e.target.value)}
                                        />
                                        {formErrors.subjectMessage && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.subjectMessage}</Typography>}
                                    </Box>

                                    <Box sx={{ marginTop: '20px', width: '600px' }}>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography mb={1}>Schema Fields</Typography>
                                            <IconButton onClick={handleAddField}>
                                                <ReactSVG
                                                    src={IconSvg.addIcon}
                                                    beforeInjection={(svg) => {
                                                        svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                                                        svg.setAttribute('stroke', '#0B51C5');
                                                    }}
                                                />
                                            </IconButton>
                                        </Box>

                                        <Box sx={{ border: '1px solid #D9D9D9', borderRadius: '5px', padding: '20px', width: '600px' }}>
                                            {(schemaFields || [])?.length > 0
                                                ?
                                                (schemaFields || []).map((field, index) => (
                                                    <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>

                                                        <TextField
                                                            label="Field Name"
                                                            value={field.fieldName}
                                                            size='small'
                                                            sx={{ width: '35%' }}
                                                            error={!!schemaErrors[index]?.fieldName}
                                                            helperText={schemaErrors[index]?.fieldName || ""}
                                                            onChange={(e) => handleChange(index, 'fieldName', e.target.value)}
                                                        />

                                                        <TextField
                                                            label="Description"
                                                            value={field.description}
                                                            size='small'
                                                            sx={{ width: '65%' }}
                                                            error={!!schemaErrors[index]?.description}
                                                            helperText={schemaErrors[index]?.description || ""}
                                                            onChange={(e) => handleChange(index, 'description', e.target.value)}
                                                        />

                                                        <IconButton onClick={() => handleDelete(index)} sx={{ width: '5%' }} >
                                                            <ReactSVG
                                                                src={IconSvg.deleteIcon}
                                                                beforeInjection={(svg) => {
                                                                    svg.setAttribute('style', 'width:18px; height:18px; display:block;');
                                                                    svg.setAttribute('stroke', '#C30E2E');
                                                                }}
                                                            />
                                                        </IconButton>

                                                    </Box>
                                                ))
                                                :
                                                (<Typography textAlign={'center'}>No Fields </Typography>)
                                            }
                                        </Box>

                                    </Box>

                                </Box>)
                            }

                            {(selectedIntegration?.payload?.type === "aws_s3") &&
                                (<Box sx={{ border: '1px solid #D9D9D9', borderRadius: '5px', padding: '15px', marginTop: '20px', width: '600px' }}>
                                    <Typography sx={{ fontSize: "15px", fontWeight: "600" }}>Import Action</Typography>
                                    <Box mt={2}>
                                        <Typography marginBottom={'4px'}>Check for Import Every <span style={{ color: 'red' }}>*</span></Typography>
                                        <RadioGroup
                                            aria-labelledby="demo-radio-buttons-group-label"
                                            style={{ display: 'flex', flexDirection: 'row' }}
                                            value={importInterval}
                                            onChange={(e) => setImportInterval(e.target.value)}
                                        >
                                            <FormControlLabel value="five_min" control={<Radio sx={{ color: '#0B51C5' }} />} label="5 minutes" />
                                            <FormControlLabel value="hour" control={<Radio sx={{ color: '#0B51C5' }} />} label="1 hour" />
                                        </RadioGroup>
                                    </Box>
                                </Box>
                                )
                            }

                            <Box mt={2} width={'600px'}>
                                <Typography mb={1}>Assign Webhook</Typography>
                                <Autocomplete
                                    multiple
                                    id="checkboxes-tags-demo"
                                    options={webhookIntegrationData || []}
                                    disableCloseOnSelect
                                    getOptionLabel={(option) => option?.payload?.name}
                                    value={selectedWebhooks}
                                    onChange={(event, newValue) => setSelectedWebhooks(newValue)}
                                    renderOption={(props, option, { selected }) => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <li key={key} {...optionProps}>
                                                <Checkbox
                                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                    checkedIcon={<CheckBox fontSize="small" />}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                />
                                                {option?.payload?.name}
                                            </li>
                                        );
                                    }}
                                    style={{ width: '100%' }}
                                    renderInput={(params) => (
                                        <TextField {...params} />
                                    )}
                                />
                                {/* {formErrors.webhookIntegration && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.webhookIntegration}</Typography>} */}
                            </Box>

                        </Box>
                    }
                </Box>
            </Box>
        </Box >
    )
}

export default AddTrigger;